<template>
  <Combobox
    input-id="searchBar-input"
    :popular-searches="popularSearches || []"
    :suggestions="suggestions"
    @change="changeHandler"
    @submit="submitHandler"
  />
</template>

<script setup lang="ts">
import { useRouter } from '#imports'

import { searchAPI } from '@backmarket/http-api'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useVisitorId } from '@backmarket/nuxt-module-identification/useVisitorId'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import { ROUTES } from '~/scopes/product-list/routes.constants'

import { useSearchIndexByQuery } from '../composables/useSearch'
import { getSearchApiKey, getSearchConfiguration } from '../helpers/helpers'

import Combobox, { type Item } from './components/Combobox.vue'

const { trackFormSubmit, trackSearchAnalytics, trackSearchTerm } = useTracking()
const router = useRouter()
const visitorId = useVisitorId()

const { data: popularSearches } = useHttpLazyFetch(searchAPI.getPopular, {
  default: () => [],
})

const searchConfig = await getSearchConfiguration('search-bar')
const apiKeyConfig = await getSearchApiKey(searchConfig.indexType)

const {
  query: searchQuery,
  queryID,
  results: suggestions,
} = useSearchIndexByQuery('', {
  index: searchConfig.indexes.active,
  apiKey: apiKeyConfig.apiKey,
})

function changeHandler(query: string) {
  searchQuery.value = query
}

function goToSearchLP({ query }: { query: string }) {
  const link = {
    type: 'internal',
    name: ROUTES.SEARCH_RESULTS,
    query: { q: query },
  }
  router.push(toNuxtLink(link))
}

function goToProductLP({ index, item }: { index: string; item: Item }) {
  if (item.objectID) {
    trackSearchAnalytics({
      type: 'click',
      objectId: item.objectID,
      searchQueryID: queryID.value,
      tokenId: visitorId,
      position: index + 1,
      index: searchConfig.indexes.active.name,
    })
  }

  router.push(toNuxtLink(item.link))
}

function submitHandler({
  query,
  index,
  item,
}: {
  query: string
  index: string
  item: Item
}) {
  trackFormSubmit({
    zone: 'header_searchbar',
    name: item?.title ?? query,
    payload: {
      index_used: index,
      search_type: item?.source ?? 'userInput',
    },
  })

  trackSearchTerm(query)

  if (item) {
    goToProductLP({ index, item })
  } else if (query) {
    goToSearchLP({ query })
  }
}
</script>
